import cn from "mcn";
import { useConnectionIsHttp1 } from "../chart/hooks/useConnectionProtocol";
import { useEffect, useState } from "react";
import { useChartDataContext } from "../context";
import { errorMessageToHumanReadable } from "../../../context/quest/api";
import { useDiagnosticsModal } from "../../diagnostics/Diagnostics";
import { useContactSupportModal } from "../../infoSection/ContactUs";
import { FaExclamationTriangle } from "react-icons/fa";

export const ChartOverlay = ({
  loading,
  error,
  children,
  empty,
}: {
  loading: boolean;
  error: Error | null;
  children: React.ReactNode;
  empty: boolean;
}) => {
  const { chart } = useChartDataContext();
  const http1 = useConnectionIsHttp1();
  const [isLoadSlow, setIsLoadSlow] = useState(false);
  const overlayVisible = loading || error || empty;
  const showError = error && empty ? error : null;
  const connectionError = isLoadSlow
    ? "Your connection appears to be slow or unstable. Please try refreshing or having less charts on this page."
    : null;
  const cls = cn(
    "absolute inset-0 flex items-center justify-center z-10 p-6 bg-[var(--color-card-muted)] transition-opacity duration-200",
    [!!overlayVisible, "opacity-100", "opacity-0 pointer-events-none"],
  );

  useEffect(() => {
    setTimeout(() => {
      if (!showError && (loading || document.getElementById(`${chart?.id}-loading`)) && http1) {
        setIsLoadSlow(true);
      }
    }, 15_000);
  }, [http1, loading, chart?.id, showError]);

  const inner = (() => {
    if (loading) {
      if (connectionError) return <ErrorMessage message={connectionError} connection />;
      return (
        <div id={`${chart?.id}-loading`} className="animate-pulse">
          Loading ...
        </div>
      );
    }

    if (showError) {
      return <ErrorMessage message={showError.message} />;
    }

    if (connectionError) return <ErrorMessage message={connectionError} connection />;
    if (empty) return <div className="text-muted-foreground">No data</div>;
    return null;
  })();

  return (
    <>
      <div className={cls}>{inner}</div>
      {children}
    </>
  );
};

function ErrorMessage({ message, connection }: { message: string; connection?: boolean }) {
  const diagnosticsModal = useDiagnosticsModal();
  const contactSupportModal = useContactSupportModal({ diagnosticsModal });

  const error = connection
    ? {
        title: "Connection issue",
        description: message,
        contact: false,
      }
    : errorMessageToHumanReadable(message);

  if (!error) return null;

  return (
    <div>
      <div className="text-center flex flex-col items-center gap-2">
        <div>
          <FaExclamationTriangle className="text-warning text-[32px]" />
        </div>
        <div className="text-lg text-foreground font-semibold">{error.title}</div>
        <div className="text-foreground">{error.description}</div>
        {error.contact && (
          <div className="text-foreground flex items-center gap-1.5">
            Please edit your chart or{" "}
            <div
              className="text-neutral underline cursor-pointer hover:text-neutral-hover"
              onClick={() => {
                contactSupportModal.open();
              }}
            >
              Contact Support
            </div>{" "}
            for more information.
          </div>
        )}
      </div>
    </div>
  );
}
